"use client";
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import { RxHamburgerMenu } from "react-icons/rx";
import { IoClose } from "react-icons/io5";

export const MobileNav = ({
  navItems,
}: {
  navItems: {
    name: string;
    link: string;
    icon?: JSX.Element;
  }[];
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => setIsOpen(false);

  return (
    <div className="md:hidden fixed top-4 right-5 z-[5001]">
      {/* Hamburger Button */}
      <motion.button
        onClick={toggleMenu}
        className="p-2.5 rounded-lg border border-white/20 bg-white/10 hover:bg-white/20 backdrop-blur-md transition-all duration-300 shadow-lg hover:shadow-xl"
        style={{ backdropFilter: "blur(12px)" }}
        aria-label="Toggle navigation menu"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <IoClose className="w-5 h-5 text-white" />
            </motion.div>
          ) : (
            <motion.div
              key="menu"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <RxHamburgerMenu className="w-5 h-5 text-white" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Mobile Navigation Drawer */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={closeMenu}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm"
              style={{
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backdropFilter: "blur(8px)",
              }}
            />

            {/* Slide-in Drawer */}
            <motion.nav
              initial={{ x: 400 }}
              animate={{ x: 0 }}
              exit={{ x: 400 }}
              transition={{ type: "spring", damping: 25, stiffness: 120 }}
              className="fixed right-0 top-0 bottom-0 w-64 sm:w-72 border-l border-white/20 shadow-2xl flex flex-col"
              style={{
                backdropFilter: "blur(20px)",
                background:
                  "linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 50%, rgba(255, 255, 255, 0.1) 100%)",
              }}
            >
              {/* Close Button - Top */}
              <div className="flex justify-end p-4 border-b border-white/15">
                <motion.button
                  onClick={closeMenu}
                  className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <IoClose className="w-5 h-5 text-white" />
                </motion.button>
              </div>

              {/* Navigation Links - Scrollable */}
              <div className="flex-1 overflow-y-auto flex flex-col space-y-1 p-4">
                {navItems.map((item, idx) => (
                  <motion.div
                    key={`mobile-link-${idx}`}
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.08, duration: 0.4 }}
                  >
                    <Link
                      href={item.link}
                      onClick={closeMenu}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg text-white/90 hover:bg-white/15 hover:text-white hover:border-white/30 border border-transparent transition-all duration-300 group font-medium"
                    >
                      {item.icon && (
                        <span className="text-purple/60 group-hover:text-purple transition-colors text-lg">
                          {item.icon}
                        </span>
                      )}
                      <span className="group-hover:translate-x-1 transition-transform duration-300">
                        {item.name}
                      </span>
                    </Link>
                  </motion.div>
                ))}
              </div>

              {/* Divider */}
              <div className="mx-4 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

              {/* Footer Info */}
              <div
                className="p-4 border-t border-white/15"
                style={{ backdropFilter: "blur(20px)" }}
              >
                <p className="text-xs text-white-200 text-center leading-relaxed">
                  © 2026 Saugata Nath
                  <br />
                  Full Stack Developer
                </p>
              </div>
            </motion.nav>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};
